import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '../components/ui/dialog';
import { toast } from 'sonner';
import { 
  DollarSign, 
  Plus, 
  Clock,
  Building2,
  FileText,
  AlertTriangle,
  Loader2,
  Check,
  XCircle,
  Calendar,
  History
} from 'lucide-react';

const RATE_TYPES = [
  { value: 'standard', label: 'Standard Rate' },
  { value: 'weekend', label: 'Weekend Rate' },
  { value: 'public_holiday', label: 'Public Holiday Rate' },
  { value: 'custom', label: 'Custom Rate' }
];

const SCOPE_OPTIONS = [
  { value: 'global', label: 'Global (Default)' },
  { value: 'site', label: 'Site-Specific' },
  { value: 'contract', label: 'Contract-Specific' }
];

export default function PayRatesTab({ officer, onRateChange }) {
  const { api } = useAuth();
  const [rates, setRates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sites, setSites] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  const [formData, setFormData] = useState({
    rate_type: 'standard',
    hourly_rate: '',
    effective_from: new Date().toISOString().split('T')[0],
    effective_to: '',
    scope: 'global',
    site_id: '',
    contract_id: '',
    notes: ''
  });

  const fetchRates = useCallback(async () => {
    if (!officer?.id) return;
    
    try {
      setLoading(true);
      const response = await api.get(`/contractor-rates/officers/${officer.id}/rates?include_expired=${showHistory}`);
      setRates(response.data.rates || []);
    } catch (error) {
      console.error('Error fetching rates:', error);
      toast.error('Failed to load pay rates');
    } finally {
      setLoading(false);
    }
  }, [api, officer?.id, showHistory]);

  const fetchSitesAndContracts = useCallback(async () => {
    try {
      const [sitesRes, contractsRes] = await Promise.all([
        api.get('/sites'),
        api.get('/contracts')
      ]);
      const siteRows = Array.isArray(sitesRes.data)
        ? sitesRes.data
        : (
          sitesRes.data?.sites
          || sitesRes.data?.items
          || sitesRes.data?.data
          || []
        );

      const contractRows = Array.isArray(contractsRes.data)
        ? contractsRes.data
        : (
          contractsRes.data?.contracts
          || contractsRes.data?.items
          || contractsRes.data?.data
          || []
        );

      setSites(
        siteRows.map((site) => ({
          ...site,
          id: String(site.id || site._id || ''),
          name: (
            site.name
            || site.site_name
            || 'Unnamed site'
          ),
        })).filter((site) => site.id),
      );

      setContracts(
        contractRows.map((contract) => ({
          ...contract,
          id: String(contract.id || contract._id || ''),
          name: (
            contract.name
            || contract.contract_name
            || contract.title
            || 'Unnamed contract'
          ),
        })).filter((contract) => contract.id),
      );
    } catch (error) {
      console.error('Error fetching sites/contracts:', error);
    }
  }, [api]);

  useEffect(() => {
    fetchRates();
    fetchSitesAndContracts();
  }, [fetchRates, fetchSitesAndContracts]);

  const resetForm = () => {
    setFormData({
      rate_type: 'standard',
      hourly_rate: '',
      effective_from: new Date().toISOString().split('T')[0],
      effective_to: '',
      scope: 'global',
      site_id: '',
      contract_id: '',
      notes: ''
    });
  };

  const isPastDate = (dateStr) => {
    const today = new Date().toISOString().split('T')[0];
    return dateStr < today;
  };

  const handleCreateRate = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!formData.hourly_rate || parseFloat(formData.hourly_rate) <= 0) {
      toast.error('Please enter a valid hourly rate');
      return;
    }

    if (!formData.effective_from) {
      toast.error('Effective date is required');
      return;
    }

    // Warn if past date
    if (isPastDate(formData.effective_from)) {
      if (!window.confirm('You are creating a rate with a past effective date. This may affect historical payroll calculations. Continue?')) {
        return;
      }
    }

    // Check for overlapping rates
    const activeRates = rates.filter(r => r.is_active && r.rate_type === formData.rate_type && r.scope === formData.scope);
    if (activeRates.length > 0) {
      const existingRate = activeRates[0];
      if (!window.confirm(`An active ${formData.rate_type} rate ($${existingRate.hourly_rate}/hr) already exists for this scope. Creating a new rate will close the existing one. Continue?`)) {
        return;
      }
    }

    setIsSubmitting(true);
    
    try {
      const payload = {
        officer_id: officer.id,
        rate_type: formData.rate_type,
        hourly_rate: parseFloat(formData.hourly_rate),
        effective_from: formData.effective_from,
        effective_to: formData.effective_to || null,
        site_id: formData.scope === 'site' ? formData.site_id : null,
        contract_id: formData.scope === 'contract' ? formData.contract_id : null,
        notes: formData.notes || null
      };

      await api.post('/contractor-rates/rates', payload);
      toast.success('Pay rate created successfully');
      setShowCreateDialog(false);
      resetForm();
      fetchRates();
      if (onRateChange) onRateChange();
    } catch (error) {
      console.error('Error creating rate:', error);
      toast.error(error.response?.data?.detail || 'Failed to create rate');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseRate = async (rateId, rateName) => {
    const closeDate = window.prompt('Enter effective end date (YYYY-MM-DD):', new Date().toISOString().split('T')[0]);
    if (!closeDate) return;

    try {
      await api.post(`/contractor-rates/rates/${rateId}/close?effective_to=${closeDate}`);
      toast.success('Rate closed successfully');
      fetchRates();
      if (onRateChange) onRateChange();
    } catch (error) {
      console.error('Error closing rate:', error);
      toast.error(error.response?.data?.detail || 'Failed to close rate');
    }
  };

  // Check if officer is eligible for contractor rates
  const isEligible = officer?.employee_type === 'contractor' || officer?.employee_type === 'casual';

  if (!isEligible) {
    return (
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardContent className="p-8 text-center">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4 text-yellow-400/50" />
          <p className="text-zinc-400">
            Contractor rates are only available for <span className="text-yellow-400">contractor</span> or <span className="text-yellow-400">casual</span> employee types.
          </p>
          <p className="text-sm text-zinc-500 mt-2">
            Current type: <Badge className="ml-2">{officer?.employee_type || 'employee'}</Badge>
          </p>
        </CardContent>
      </Card>
    );
  }

  // Get current active rate
  const activeRates = rates.filter(r => r.is_active);
  const currentStandardRate = activeRates.find(r => r.rate_type === 'standard' && r.scope === 'global');

  return (
    <div className="space-y-4" data-testid="pay-rates-tab">
      {/* Current Active Rate Card */}
      <Card className="bg-gradient-to-br from-emerald-500/10 to-emerald-500/5 border-emerald-500/30">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm font-rajdhani text-emerald-400 uppercase tracking-wider flex items-center gap-2">
            <DollarSign className="w-4 h-4" />
            Current Active Rate
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentStandardRate ? (
            <div className="flex items-center justify-between">
              <div>
                <span className="text-3xl font-bold text-white">${currentStandardRate.hourly_rate}</span>
                <span className="text-zinc-400 ml-1">/hr</span>
                <div className="text-sm text-zinc-500 mt-1">
                  Effective from {currentStandardRate.effective_from}
                  {currentStandardRate.effective_to && ` to ${currentStandardRate.effective_to}`}
                </div>
              </div>
              <Badge className={`${currentStandardRate.scope === 'global' ? 'bg-blue-500/20 text-blue-400' : currentStandardRate.scope === 'site' ? 'bg-purple-500/20 text-purple-400' : 'bg-orange-500/20 text-orange-400'}`}>
                {currentStandardRate.scope}
              </Badge>
            </div>
          ) : (
            <div className="text-zinc-500 flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-yellow-400" />
              No active standard rate. Using system fallback.
            </div>
          )}
        </CardContent>
      </Card>

      {/* Actions */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Button
            variant={showHistory ? "default" : "outline"}
            size="sm"
            onClick={() => setShowHistory(!showHistory)}
            className={showHistory ? "bg-zinc-700" : "border-zinc-600"}
          >
            <History className="w-4 h-4 mr-2" />
            {showHistory ? 'Hide History' : 'Show History'}
          </Button>
        </div>
        
        <Button
          onClick={() => setShowCreateDialog(true)}
          className="bg-emerald-500 hover:bg-emerald-600 text-white"
          data-testid="add-rate-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          Add Rate
        </Button>
      </div>

      {/* Rates Table */}
      <Card className="bg-[#18181b] border-[#3f3f46]">
        <CardHeader className="border-b border-[#3f3f46] py-3 px-4">
          <CardTitle className="text-sm font-rajdhani text-zinc-300 uppercase tracking-wider flex items-center gap-2">
            <Clock className="w-4 h-4 text-blue-400" />
            {showHistory ? 'Rate History' : 'Active Rates'} ({rates.length})
          </CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          {loading ? (
            <div className="p-8 text-center">
              <Loader2 className="h-8 w-8 animate-spin mx-auto text-blue-500" />
            </div>
          ) : rates.length === 0 ? (
            <div className="p-8 text-center text-zinc-500">
              No pay rates configured yet.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-[#3f3f46] bg-[#27272a]/50">
                    <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Type</th>
                    <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Rate</th>
                    <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Scope</th>
                    <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Effective From</th>
                    <th className="text-left p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Effective To</th>
                    <th className="text-center p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Status</th>
                    <th className="text-right p-3 text-xs font-rajdhani font-semibold text-zinc-400 uppercase">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-[#3f3f46]">
                  {rates.map((rate) => (
                    <tr 
                      key={rate.id} 
                      className={`hover:bg-[#27272a]/50 transition-colors ${!rate.is_active ? 'opacity-50' : ''}`}
                      data-testid={`rate-row-${rate.id}`}
                    >
                      <td className="p-3">
                        <span className="text-sm text-zinc-200 capitalize">{rate.rate_type.replace('_', ' ')}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm font-mono text-emerald-400 font-bold">${rate.hourly_rate}/hr</span>
                      </td>
                      <td className="p-3">
                        <Badge className={`text-xs ${rate.scope === 'global' ? 'bg-blue-500/20 text-blue-400' : rate.scope === 'site' ? 'bg-purple-500/20 text-purple-400' : 'bg-orange-500/20 text-orange-400'}`}>
                          {rate.scope}
                        </Badge>
                        {rate.site_name && (
                          <span className="text-xs text-zinc-500 ml-2">{rate.site_name}</span>
                        )}
                        {rate.contract_name && (
                          <span className="text-xs text-zinc-500 ml-2">{rate.contract_name}</span>
                        )}
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-zinc-400 font-mono">{rate.effective_from}</span>
                      </td>
                      <td className="p-3">
                        <span className="text-sm text-zinc-400 font-mono">{rate.effective_to || '-'}</span>
                      </td>
                      <td className="p-3 text-center">
                        {rate.is_active ? (
                          <Badge className="bg-emerald-500/20 text-emerald-400 text-xs">
                            <Check className="w-3 h-3 mr-1" />
                            Active
                          </Badge>
                        ) : (
                          <Badge className="bg-zinc-500/20 text-zinc-400 text-xs">
                            <XCircle className="w-3 h-3 mr-1" />
                            Expired
                          </Badge>
                        )}
                      </td>
                      <td className="p-3 text-right">
                        {rate.is_active && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleCloseRate(rate.id, rate.rate_type)}
                            className="text-zinc-400 hover:text-red-400"
                            data-testid={`close-rate-${rate.id}`}
                          >
                            <XCircle className="w-4 h-4" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create Rate Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={(open) => {
        setShowCreateDialog(open);
        if (!open) resetForm();
      }}>
        <DialogContent className="bg-[#18181b] border-[#3f3f46] text-white max-w-lg">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-xl">ADD PAY RATE</DialogTitle>
            <DialogDescription className="text-zinc-500">
              Create a new pay rate for {officer?.first_name} {officer?.last_name}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateRate} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Rate Type <span className="text-red-400">*</span></Label>
                <Select 
                  value={formData.rate_type} 
                  onValueChange={(v) => setFormData({...formData, rate_type: v})}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {RATE_TYPES.map(rt => (
                      <SelectItem key={rt.value} value={rt.value}>{rt.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Hourly Rate <span className="text-red-400">*</span></Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500">$</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.hourly_rate}
                    onChange={(e) => setFormData({...formData, hourly_rate: e.target.value})}
                    className="bg-[#09090b] border-[#3f3f46] pl-8"
                    placeholder="0.00"
                    required
                    disabled={isSubmitting}
                    data-testid="rate-hourly-input"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Scope <span className="text-red-400">*</span></Label>
              <Select 
                value={formData.scope} 
                onValueChange={(v) => setFormData({...formData, scope: v, site_id: '', contract_id: ''})}
                disabled={isSubmitting}
              >
                <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                  {SCOPE_OPTIONS.map(opt => (
                    <SelectItem key={opt.value} value={opt.value}>{opt.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {formData.scope === 'site' && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Site <span className="text-red-400">*</span></Label>
                <Select 
                  value={formData.site_id} 
                  onValueChange={(v) => setFormData({...formData, site_id: v})}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                    <Building2 className="w-4 h-4 mr-2 text-zinc-500" />
                    <SelectValue placeholder="Select site..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {sites.map(site => (
                      <SelectItem key={site.id} value={site.id}>{site.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {formData.scope === 'contract' && (
              <div className="space-y-2">
                <Label className="text-zinc-300">Contract <span className="text-red-400">*</span></Label>
                <Select 
                  value={formData.contract_id} 
                  onValueChange={(v) => setFormData({...formData, contract_id: v})}
                  disabled={isSubmitting}
                >
                  <SelectTrigger className="bg-[#09090b] border-[#3f3f46]">
                    <FileText className="w-4 h-4 mr-2 text-zinc-500" />
                    <SelectValue placeholder="Select contract..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#18181b] border-[#3f3f46]">
                    {contracts.map(contract => (
                      <SelectItem key={contract.id} value={contract.id}>{contract.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">Effective From <span className="text-red-400">*</span></Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="date"
                    value={formData.effective_from}
                    onChange={(e) => setFormData({...formData, effective_from: e.target.value})}
                    className="bg-[#09090b] border-[#3f3f46] pl-10"
                    required
                    disabled={isSubmitting}
                    data-testid="rate-effective-from"
                  />
                </div>
                {isPastDate(formData.effective_from) && (
                  <p className="text-xs text-yellow-400 flex items-center gap-1">
                    <AlertTriangle className="w-3 h-3" />
                    Past date selected
                  </p>
                )}
              </div>
              <div className="space-y-2">
                <Label className="text-zinc-300">Effective To (Optional)</Label>
                <div className="relative">
                  <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                  <Input
                    type="date"
                    value={formData.effective_to}
                    onChange={(e) => setFormData({...formData, effective_to: e.target.value})}
                    className="bg-[#09090b] border-[#3f3f46] pl-10"
                    disabled={isSubmitting}
                    data-testid="rate-effective-to"
                  />
                </div>
              </div>
            </div>

            <div className="space-y-2">
              <Label className="text-zinc-300">Notes</Label>
              <Input
                value={formData.notes}
                onChange={(e) => setFormData({...formData, notes: e.target.value})}
                className="bg-[#09090b] border-[#3f3f46]"
                placeholder="Optional notes..."
                disabled={isSubmitting}
              />
            </div>

            <DialogFooter>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowCreateDialog(false)}
                className="border-zinc-600"
                disabled={isSubmitting}
              >
                Cancel
              </Button>
              <Button 
                type="submit" 
                className="bg-emerald-500 hover:bg-emerald-600"
                disabled={isSubmitting}
                data-testid="submit-rate-btn"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  'Create Rate'
                )}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
